import Tooltip from "@components/tooltip";
import { BackgroundJobStatusCode } from "@odata/GeneratedEnums";
import { isDefined } from "@utils/general";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { addCompanyIdToUrl } from "../../contexts/appContext/AppContext.utils";
import { BackgroundJobActionType } from "../../contexts/backgroundJobsContext/BackgroundJobsContext.utils";
import { IconSize, QueryParam } from "../../enums";
import { ROUTE_BACKGROUND_JOB } from "../../routes";
import TestIds from "../../testIds";
import Avatar from "../avatar";
import { CloseIcon } from "../icon";
import { IProps as IProgressBarProps } from "../progressBar";
import ProgressBar from "../progressBar/ProgressBar";
import {
    BackgroundJobActionOverlay,
    BackgroundJobHeaderText,
    BackgroundJobHeaderWrapper,
    BackgroundJobSeparator,
    BackgroundJobTitle,
    ButtonBackgroundJobAction,
    IconButtonBackgroundJobClose,
    StyledBackgroundJob
} from "./BackgroundJobsPopup.styles";

export interface IBackgroundJobProps {
    id: number;
    title: string;
    progress: IProgressBarProps;
    avatarSrc: string;
    headerText: string;
    hideCloseButton?: boolean;
    /** On close button click */
    onClose?: (id: number) => void;
    /** Triggers hide transition */
    isClosing?: boolean;

    companyId?: number;

    /** Called on transition end */
    onCloseFinish?: (id: number) => void;

    isResultReadable?: boolean;
    statusCode?: BackgroundJobStatusCode;

    action?: BackgroundJobActionType;
    onActionClick?: (backgroundJobId: number, actionId: BackgroundJobActionType) => void;
}

type Props = IBackgroundJobProps & RouteComponentProps;

const BackgroundJobComponent: React.FunctionComponent<Props> = React.memo((props) => {
    const isFinished = [BackgroundJobStatusCode.Canceled, BackgroundJobStatusCode.Error, BackgroundJobStatusCode.Expired,
        BackgroundJobStatusCode.Finished, BackgroundJobStatusCode.FinishedWithErrors,].includes(props.statusCode);

    const { id, onClose, action, onActionClick, isClosing, onCloseFinish } = props;
    const { t } = useTranslation(["Common", "Components"]);
    const fnOnClose = useCallback((args: React.MouseEvent) => {
        args.stopPropagation();
        onClose(id);
    }, [id, onClose]);
    const fnOnTransitionEnd = useCallback(() => {
        if (isClosing) {
            onCloseFinish(id);
        }
    }, [id, onCloseFinish, isClosing]);
    const fnOnActionClick = useCallback(() => {
        onActionClick(id, action);
    }, [onActionClick, id, action]);

    const fnHandleJobClick = useCallback(() => {
        if (props.isResultReadable && isFinished) {
            const url = addCompanyIdToUrl(`${ROUTE_BACKGROUND_JOB}/${id}?${QueryParam.DefaultTab}=null`, props.companyId);
            onActionClick(id, BackgroundJobActionType.Redirect);
            props.history.push(url);
        }
    }, [props.isResultReadable, id, onActionClick, props.history, isFinished]);

    return (
        <>
            <StyledBackgroundJob key={id}
                                 onClick={fnHandleJobClick}
                                 $isClosing={props.isClosing}
                                 $isLink={props.isResultReadable && isFinished}
                                 $hasAction={!!props.action}
                                 onTransitionEnd={fnOnTransitionEnd}
                                 tabIndex={0}
                                 data-testid={TestIds.BackgroundJobsItem}>
                {!!props.action &&
                    <BackgroundJobActionOverlay/>
                }
                <BackgroundJobHeaderWrapper>
                    {props.avatarSrc &&
                        <Avatar src={props.avatarSrc} size={"14px"}/>
                    }
                    <BackgroundJobHeaderText>
                        {props.headerText}
                    </BackgroundJobHeaderText>
                </BackgroundJobHeaderWrapper>
                <Tooltip content={props.title} onlyShowWhenChildrenOverflowing>
                    {(ref) => (
                        <BackgroundJobTitle data-testid={TestIds.Title} ref={ref}>
                            {props.title}
                        </BackgroundJobTitle>
                    )}
                </Tooltip>

                {!props.hideCloseButton &&
                    <IconButtonBackgroundJobClose title={t("Common:General.Close")}
                                                  isDecorative
                                                  onClick={fnOnClose}>
                        <CloseIcon
                            width={IconSize.S}
                            height={IconSize.S}/>
                    </IconButtonBackgroundJobClose>
                }
                <ProgressBar {...props.progress} isDescriptionInverseValue/>
                {props.action &&
                    <ButtonBackgroundJobAction
                        onClick={fnOnActionClick}>
                        {t(`Components:BackgroundJobs.${props.action}`)}
                    </ButtonBackgroundJobAction>
                }
            </StyledBackgroundJob>
            <BackgroundJobSeparator/>
        </>
    );
});

export const BackgroundJob = withRouter(BackgroundJobComponent);